import React, { useState, useEffect, useRef } from 'react';
import { X, Download, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import WhatsAppSendModal from './ProformaWhatsAppModal';
import DownloadFormatModal from './DownloadFormatModal';

// API base URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * ProformaInvoicePreviewModal Component
 * Displays a proforma invoice PDF rendered as an image using PDF.js
 * Similar to ReceiptPreviewModal but for wholesale proforma invoices
 */
const ProformaInvoicePreviewModal = ({ order, onClose, onSuccess }) => {
  const [pageImageUrls, setPageImageUrls] = useState([]);
  const [loadingImage, setLoadingImage] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const pdfLibRef = useRef(null); // Cache PDF.js library for performance

  // WhatsApp modal state
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  
  // Download format modal state
  const [formatModalOpen, setFormatModalOpen] = useState(false);

  useEffect(() => {
    if (order && order.id) {
      loadProformaAsPdf();
    }
  }, [order]);

  /**
   * Load proforma PDF and render to canvas as image using dynamic import
   */
  const loadProformaAsPdf = async () => {
    if (!order || !order.id) {
      console.error('ProformaInvoicePreviewModal: No order provided');
      setLoadError('No order provided');
      setLoadingImage(false);
      return;
    }

    if (!order.proforma_invoice_number) {
      setLoadError('No proforma invoice generated yet');
      setLoadingImage(false);
      return;
    }

    console.log(`[Proforma] Rendering proforma invoice for order ${order.id}...`);
    setLoadingImage(true);
    setLoadError(null);

    try {
      // Fetch the PDF blob from the backend using the proper API URL
      const response = await fetch(`${API_URL}/wholesale/orders/${order.id}/proforma-invoice/pdf`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch proforma invoice: ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('ProformaInvoicePreviewModal: Received blob:', blob?.size, 'bytes, type:', blob?.type);

      if (!blob || blob.size === 0) {
        throw new Error('Empty response from server');
      }

      // Check if the blob is actually a PDF
      if (blob.type && !blob.type.includes('pdf')) {
        // If it's already an image, use it directly (fallback)
        if (blob.type.includes('image')) {
          const imageUrl = URL.createObjectURL(blob);
          setProformaImageUrl(imageUrl);
          setLoadingImage(false);
          return;
        }
      }

      // Dynamic import PDF.js with caching to avoid repeated imports
      const importStart = Date.now();
      if (!pdfLibRef.current) {
        const pdfjsLib = await import('pdfjs-dist');
        // Use CDN for worker (reliable across all environments)
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
        pdfLibRef.current = pdfjsLib;
        console.log(`ProformaInvoicePreviewModal: PDF.js loaded in ${Date.now() - importStart}ms`);
      }
      const pdfjsLib = pdfLibRef.current;

      // Convert blob to ArrayBuffer for PDF.js
      const arrayBuffer = await blob.arrayBuffer();

      // Load the PDF document
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        isEvalSupported: false,
        useSystemFonts: true
      });
      const pdfDoc = await loadingTask.promise;

      const numPages = pdfDoc.numPages;
      console.log('ProformaInvoicePreviewModal: PDF loaded, pages:', numPages);

      // Render ALL pages
      const scale = 1.0;
      const pageUrls = [];

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale });

        // Create a canvas element for this page
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Render the PDF page to canvas
        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;

        // Convert canvas to JPEG blob URL
        const imageBlob = await new Promise(resolve =>
          canvas.toBlob(resolve, 'image/jpeg', 0.90)
        );
        const imageUrl = URL.createObjectURL(imageBlob);
        pageUrls.push(imageUrl);
        
        console.log(`ProformaInvoicePreviewModal: Page ${pageNum}/${numPages} rendered`);
      }

      setPageImageUrls(pageUrls);

      // Clean up
      await pdfDoc.destroy();

    } catch (error) {
      console.error('ProformaInvoicePreviewModal: Error loading proforma:', error);
      setLoadError(error.message || 'Failed to load proforma invoice');
      toast.error('Failed to load proforma invoice preview: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingImage(false);
    }
  };

  const handleDownload = () => {
    setFormatModalOpen(true);
  };

  const handleFormatSelected = async (format) => {
    setActionLoading('download');
    try {
      // Always download PDF from backend (to avoid server crashes)
      const response = await fetch(`${API_URL}/wholesale/orders/${order.id}/proforma-invoice`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download proforma invoice');
      }

      const blob = await response.blob();

      if (format === 'image') {
        // Convert PDF to PNG using PDF.js (client-side)
        console.log('[Download] Converting PDF to PNG on client...');
        const imageBlob = await convertPDFToImage(blob);
        
        // Download as PNG
        const url = window.URL.createObjectURL(imageBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `proforma-${order.proforma_invoice_number}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Proforma invoice downloaded as PNG');
      } else {
        // Download as PDF directly
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `proforma-${order.proforma_invoice_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Proforma invoice downloaded as PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download proforma invoice');
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Convert PDF blob to PNG image using PDF.js
   * This runs in the browser to avoid server memory issues
   * Combines all pages vertically into a single tall image
   * Uses intelligent header detection to properly handle multi-page documents
   */
  const convertPDFToImage = async (pdfBlob) => {
    try {
      if (!pdfLibRef.current) {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
        pdfLibRef.current = pdfjsLib;
      }
      const pdfjsLib = pdfLibRef.current;

      const arrayBuffer = await pdfBlob.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer, isEvalSupported: false, useSystemFonts: true });
      const pdfDoc = await loadingTask.promise;
      const numPages = pdfDoc.numPages;
      console.log(`[PDF to Image] Converting ${numPages} page(s)...`);

      const scale = 2.0;
      const pageCanvases = [];
      let totalHeight = 0;
      let maxWidth = 0;

      /**
       * Detect if a header row exists in the top portion of the page
       * Headers typically have: horizontal lines, consistent text patterns
       * Returns the Y position where header ends, or 0 if no header detected
       */
      const detectHeaderCropPosition = (canvas, context, imageData) => {
        const pixels = imageData.data;
        const width = canvas.width;
        const height = canvas.height;
        const threshold = 250;
        
        // Scan top 20% of page for header detection
        const scanHeight = Math.floor(height * 0.20);
        
        // Look for horizontal lines (common in table headers)
        // A horizontal line has many consecutive dark pixels in a row
        let headerEndY = 0;
        let foundHorizontalLine = false;
        let lastLineY = 0;
        
        for (let y = 0; y < scanHeight; y++) {
          let consecutiveDarkPixels = 0;
          let maxConsecutive = 0;
          
          for (let x = 0; x < width; x++) {
            const index = (y * width + x) * 4;
            const isDark = pixels[index] < threshold || pixels[index + 1] < threshold || pixels[index + 2] < threshold;
            
            if (isDark) {
              consecutiveDarkPixels++;
              maxConsecutive = Math.max(maxConsecutive, consecutiveDarkPixels);
            } else {
              consecutiveDarkPixels = 0;
            }
          }
          
          // If we find a row with a horizontal line (>60% of width is dark in sequence)
          // this indicates a table header separator
          if (maxConsecutive > width * 0.6) {
            foundHorizontalLine = true;
            lastLineY = y;
          }
        }
        
        // If we found a horizontal line, crop from just below it
        if (foundHorizontalLine && lastLineY > 10) {
          headerEndY = lastLineY + Math.floor(5 * scale);
          console.log(`[Header Detection] Found horizontal line separator at Y=${lastLineY}, cropping from ${headerEndY}`);
          return headerEndY;
        }
        
        // Alternative detection: Look for the typical header pattern
        // (column headers like "ITEM", "QTY", "PRICE", "TOTAL")
        // These usually appear in the first 15% of the page with specific spacing
        
        // Count content rows in top portion - headers have specific density
        let contentRowsInTop = 0;
        let lastContentY = 0;
        for (let y = 0; y < scanHeight; y++) {
          let hasContent = false;
          for (let x = Math.floor(width * 0.1); x < Math.floor(width * 0.9); x++) {
            const index = (y * width + x) * 4;
            if (pixels[index] < threshold || pixels[index + 1] < threshold || pixels[index + 2] < threshold) {
              hasContent = true;
              break;
            }
          }
          if (hasContent) {
            // Only count if there's a gap from previous content (indicates separate row)
            if (y - lastContentY > 5) {
              contentRowsInTop++;
            }
            lastContentY = y;
          }
        }
        
        // If we detect 2-4 distinct text sections in top 20%, likely a header
        // Headers typically have: Company info + Doc title + Column headers
        if (contentRowsInTop >= 3 && contentRowsInTop <= 6) {
          // Find the gap after the header section (where data rows begin)
          let inGap = false;
          let gapStart = 0;
          for (let y = Math.floor(scanHeight * 0.3); y < scanHeight; y++) {
            let rowHasContent = false;
            for (let x = 0; x < width; x++) {
              const index = (y * width + x) * 4;
              if (pixels[index] < threshold || pixels[index + 1] < threshold || pixels[index + 2] < threshold) {
                rowHasContent = true;
                break;
              }
            }
            
            if (!rowHasContent && !inGap) {
              inGap = true;
              gapStart = y;
            } else if (rowHasContent && inGap) {
              // Found content after a gap - this is where data begins
              if (y - gapStart > 10 * scale) {
                console.log(`[Header Detection] Found header gap at Y=${gapStart}-${y}, cropping from ${y - 5}`);
                return Math.max(0, y - Math.floor(5 * scale));
              }
              inGap = false;
            }
          }
        }
        
        console.log(`[Header Detection] No clear header pattern found, not cropping top`);
        return 0;
      };

      /**
       * Check if page contains only totals/accumulation section (no data rows)
       * Totals-only pages have: sparse content, typically in top 40% only
       */
      const isTotalsOnlyPage = (canvas, context, imageData) => {
        const pixels = imageData.data;
        const width = canvas.width;
        const height = canvas.height;
        const threshold = 250;
        
        // Count content rows in different sections of the page
        let contentRowsTop40 = 0;
        let contentRowsBottom60 = 0;
        let lastContentY = -100;
        
        for (let y = 0; y < height; y++) {
          let hasContent = false;
          for (let x = Math.floor(width * 0.1); x < Math.floor(width * 0.9); x++) {
            const index = (y * width + x) * 4;
            if (pixels[index] < threshold || pixels[index + 1] < threshold || pixels[index + 2] < threshold) {
              hasContent = true;
              break;
            }
          }
          if (hasContent && y - lastContentY > 10) {
            if (y < height * 0.4) {
              contentRowsTop40++;
            } else {
              contentRowsBottom60++;
            }
            lastContentY = y;
          }
        }
        
        // Totals-only page characteristics:
        // - Very few content rows (typically 2-5 for totals section)
        // - Most/all content in top 40% of page
        const totalRows = contentRowsTop40 + contentRowsBottom60;
        const isTotalsOnly = totalRows <= 6 && contentRowsBottom60 <= 1;
        
        console.log(`[Totals Detection] Top40: ${contentRowsTop40} rows, Bottom60: ${contentRowsBottom60} rows, Total: ${totalRows}, isTotalsOnly: ${isTotalsOnly}`);
        return isTotalsOnly;
      };

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale });
        const tempCanvas = document.createElement('canvas');
        const tempContext = tempCanvas.getContext('2d');
        tempCanvas.height = viewport.height;
        tempCanvas.width = viewport.width;

        // Set white background to ensure transparency doesn't affect cropping
        tempContext.fillStyle = 'white';
        tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        await page.render({ canvasContext: tempContext, viewport: viewport }).promise;

        const imageData = tempContext.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const pixels = imageData.data;
        const threshold = 250;

        let contentTop = 0;
        let contentBottom = tempCanvas.height;

        // For pages after the first, use intelligent header detection
        if (pageNum > 1) {
          // First check if this is a totals-only page
          if (isTotalsOnlyPage(tempCanvas, tempContext, imageData)) {
            // Totals-only page - don't crop the top, just find where content starts
            for (let y = 0; y < tempCanvas.height; y++) {
              let hasContent = false;
              for (let x = 0; x < tempCanvas.width; x++) {
                const index = (y * tempCanvas.width + x) * 4;
                if (pixels[index] < threshold || pixels[index + 1] < threshold || pixels[index + 2] < threshold) {
                  hasContent = true;
                  break;
                }
              }
              if (hasContent) {
                contentTop = Math.max(0, y - Math.floor(10 * scale));
                break;
              }
            }
            console.log(`[Page ${pageNum}] Totals-only page, minimal crop from ${contentTop}px`);
          } else {
            // Page has data rows - detect and crop the header
            const headerEndY = detectHeaderCropPosition(tempCanvas, tempContext, imageData);
            if (headerEndY > 0) {
              contentTop = headerEndY;
              console.log(`[Page ${pageNum}] Header detected, cropping from ${contentTop}px`);
            } else {
              // Fallback: use minimal skip if no header detected
              const minSkip = Math.floor(tempCanvas.height * 0.02);
              for (let y = minSkip; y < tempCanvas.height; y++) {
                let hasContent = false;
                for (let x = 0; x < tempCanvas.width; x++) {
                  const index = (y * tempCanvas.width + x) * 4;
                  if (pixels[index] < threshold || pixels[index + 1] < threshold || pixels[index + 2] < threshold) {
                    hasContent = true;
                    break;
                  }
                }
                if (hasContent) {
                  contentTop = Math.max(0, y - Math.floor(5 * scale));
                  break;
                }
              }
              console.log(`[Page ${pageNum}] Fallback crop from ${contentTop}px`);
            }
          }
        }

        // Find last row with content from bottom
        for (let y = tempCanvas.height - 1; y >= contentTop; y--) {
          let hasContent = false;
          for (let x = 0; x < tempCanvas.width; x++) {
            const index = (y * tempCanvas.width + x) * 4;
            if (pixels[index] < threshold || pixels[index + 1] < threshold || pixels[index + 2] < threshold) {
              hasContent = true;
              break;
            }
          }
          if (hasContent) {
            contentBottom = y + 1;
            break;
          }
        }

        const padding = pageNum === 1 ? 20 * scale : 10 * scale;
        contentBottom = Math.min(contentBottom + padding, tempCanvas.height);

        const croppedHeight = contentBottom - contentTop;
        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = tempCanvas.width;
        croppedCanvas.height = croppedHeight;
        const croppedContext = croppedCanvas.getContext('2d');
        croppedContext.drawImage(tempCanvas, 0, contentTop, tempCanvas.width, croppedHeight, 0, 0, tempCanvas.width, croppedHeight);

        pageCanvases.push(croppedCanvas);
        totalHeight += croppedHeight;
        maxWidth = Math.max(maxWidth, tempCanvas.width);
        console.log(`[PDF to Image] Page ${pageNum}: ${tempCanvas.height}px, cropped ${contentTop}px-${contentBottom}px = ${croppedHeight}px`);
      }

      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = maxWidth;
      combinedCanvas.height = totalHeight;
      const combinedContext = combinedCanvas.getContext('2d');
      combinedContext.fillStyle = 'white';
      combinedContext.fillRect(0, 0, maxWidth, totalHeight);

      let currentY = 0;
      for (const pageCanvas of pageCanvases) {
        combinedContext.drawImage(pageCanvas, 0, currentY);
        currentY += pageCanvas.height;
      }

      const imageBlob = await new Promise(resolve => combinedCanvas.toBlob(resolve, 'image/png'));
      await pdfDoc.destroy();
      console.log(`[PDF to Image] Converted ${numPages} page(s) to PNG`);
      return imageBlob;
    } catch (error) {
      console.error('PDF to image conversion error:', error);
      throw new Error('Failed to convert PDF to image');
    }
  };

  const handleWhatsApp = () => {
    setWhatsAppModalOpen(true);
  };

  const handleRetry = () => {
    loadProformaAsPdf();
  };

  const handleWhatsAppSuccess = () => {
    toast.success('Proforma invoice sent via WhatsApp');
    if (onSuccess) {
      onSuccess();
    }
  };

  if (!order) return null;

  return (
    <>
      {/* Main Proforma Modal */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Proforma Invoice #{order.proforma_invoice_number}
              </h2>
              <p className="text-sm text-gray-600">
                Order #{order.order_number} • {order.distributor_business_name || 'Customer'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Modal Body - Proforma Image */}
          <div className="flex-1 overflow-auto p-4 bg-gray-50">
            {loadingImage ? (
              <div className="flex flex-col items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-violet-600 animate-spin mb-3" />
                <p className="text-sm text-gray-500">Loading proforma invoice...</p>
              </div>
            ) : pageImageUrls.length > 0 ? (
              <div className="flex flex-col items-center gap-4">
                {pageImageUrls.map((url, index) => (
                  <div key={index} className="relative">
                    {pageImageUrls.length > 1 && (
                      <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                        Page {index + 1} of {pageImageUrls.length}
                      </div>
                    )}
                    <img
                      src={url}
                      alt={`Proforma Invoice ${order.proforma_invoice_number} - Page ${index + 1}`}
                      className="max-w-full h-auto shadow-lg rounded-lg"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-600">
                <p className="mb-2">Failed to load proforma invoice preview</p>
                {loadError && <p className="text-xs text-red-500 mb-3">{loadError}</p>}
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* Modal Footer - Action Buttons */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleWhatsApp}
                disabled={pageImageUrls.length === 0}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Send via WhatsApp</span>
              </button>
              <button
                onClick={handleDownload}
                disabled={actionLoading === 'download'}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === 'download' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">Download PDF</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {whatsAppModalOpen && (
        <WhatsAppSendModal
          order={order}
          onClose={() => setWhatsAppModalOpen(false)}
          onSuccess={handleWhatsAppSuccess}
          generateImage={async () => {
            // Fetch PDF and convert to image using same logic as download
            const response = await fetch(`${API_URL}/wholesale/orders/${order.id}/proforma-invoice`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
              }
            });
            if (!response.ok) throw new Error('Failed to fetch proforma invoice');
            const pdfBlob = await response.blob();
            return await convertPDFToImage(pdfBlob);
          }}
        />
      )}

      {/* Download Format Modal */}
      <DownloadFormatModal
        isOpen={formatModalOpen}
        onClose={() => setFormatModalOpen(false)}
        onSelectFormat={handleFormatSelected}
        documentName={`Proforma Invoice #${order.proforma_invoice_number}`}
      />
    </>
  );
};

export default ProformaInvoicePreviewModal;
